import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  PaginatedResult,
  ServiceCategory,
  TicketStatus,
  TicketType,
  Member,
  ClickupBackfillLinksResult,
  ClickupBackfillPsaLinkResult,
  ClickupBackfillReferencesResult,
  ClickupCustomerTagMapping,
  ClickupCustomerTagSourceOption,
  ClickupReconcileResult,
  ClickupReconcileState,
  ClickupWebhookQueueDrainResult,
  ClickupWebhookQueueStatus,
  CreateClickupCustomerTagMappingInput,
  UpdateClickupReconcileInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface ClickupWorkspaceSummary {
  id: string;
  name: string;
}

export interface ClickupLogEntry {
  id: string;
  timestamp: string;
  direction: 'inbound' | 'outbound' | 'processing';
  event: string;
  status: 'received' | 'ok' | 'error' | 'skipped';
  detail: string;
  taskId?: string | null;
  nextAction?: string | null;
  payloadJson?: string | null;
  resultJson?: string | null;
  errorMessage?: string | null;
}

export interface ClickupDiscoveryMember {
  id: string;
  name: string;
  username?: string;
  email?: string;
}

export interface ClickupDiscoveryList {
  id: string;
  name: string;
  spaceId?: string;
  spaceName?: string;
}

export interface ClickupDiscoverySpace {
  id: string;
  name: string;
}

export interface ClickupDiscoveryFolder {
  id: string;
  name: string;
  spaceId: string;
  spaceName: string;
}

export interface ClickupDiscoveryStatusLocation {
  listId: string;
  listName: string;
  spaceId: string;
  spaceName: string;
}

export interface ClickupDiscoveryStatus {
  id: string;
  code: string;
  name: string;
  type?: string;
  locations: ClickupDiscoveryStatusLocation[];
}

export interface ClickupDiscoveryTemplate {
  id: string;
  name: string;
  type: 'list' | 'task';
}

export interface ClickupWorkspaceDiscovery {
  teamId: string;
  teamName: string;
  members: ClickupDiscoveryMember[];
  spaces: ClickupDiscoverySpace[];
  folders: ClickupDiscoveryFolder[];
  lists: ClickupDiscoveryList[];
  statuses: ClickupDiscoveryStatus[];
  templates: ClickupDiscoveryTemplate[];
}

export interface ClickupDiscoveryResponse {
  workspaces: ClickupWorkspaceSummary[];
  selectedTeamId: string;
  data: ClickupWorkspaceDiscovery;
}

export interface ClickupConfig {
  enabled: boolean;
  webhookSignatureConfigured: boolean;
  webhookId: string | null;
  hasApiKey: boolean;
  updatedAt: string | null;
}

export interface ClickupAuditStep {
  at: string;
  status: string;
  detail: string;
}

export interface ClickupAuditMismatch {
  kind: string;
  detail: string;
}

export interface ClickupWebhookAuditEntry {
  id: string;
  receivedAt: string;
  event: string;
  taskId: string | null;
  psaTicketId: string | null;
  outcome: string;
  summary: string;
  durationMs: number;
  steps: ClickupAuditStep[];
  mismatches: ClickupAuditMismatch[];
  errorMessage: string | null;
  hasPayload: boolean;
}

export interface ClickupWebhookAuditDetail extends ClickupWebhookAuditEntry {
  payloadJson: string | null;
}

export interface ClickupReconcileItem {
  taskId: string;
  taskName: string;
  listName: string;
  action: 'created' | 'updated' | 'skipped' | 'failed';
  reason: string;
  ticketId?: string;
}

export interface ClickupReconcileAuditEntry {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  trigger: string;
  outcome: string;
  windowStart: string | null;
  listsScanned: number;
  scanned: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  message: string | null;
  itemCount: number;
}

export interface ClickupReconcileAuditDetail extends ClickupReconcileAuditEntry {
  items: ClickupReconcileItem[];
}

export interface UpdateClickupConfigInput {
  enabled?: boolean;
  apiKey?: string | null;
  webhookSignatureSecret?: string | null;
  clickupWebhookId?: string | null;
}

export interface ClickupListMapping {
  id: string;
  clickupListId: string;
  clickupListName: string;
  ticketTypeId: string;
  ticketType: { id: string; name: string };
}

export interface ClickupMemberMapping {
  id: string;
  clickupMemberId: string;
  clickupMemberName: string;
  psaMemberId: string;
  psaMember: { id: string; displayName: string; email: string };
}

export interface ClickupStatusMapping {
  id: string;
  clickupStatusCode: string;
  clickupStatusName: string;
  ticketStatusId: string;
  ticketStatus: { id: string; name: string };
}

export interface ClickupOpportunityTypeMapping {
  id: string;
  clickupSpaceId: string;
  clickupSpaceName: string;
  clickupFolderId: string | null;
  clickupFolderName: string | null;
  clickupListTemplateId: string | null;
  clickupListTemplateName: string | null;
  opportunityTypeId: string;
  opportunityType: { id: string; name: string };
}

const keys = {
  config: ['clickup', 'config'] as const,
  lists: ['clickup', 'mappings', 'lists'] as const,
  members: ['clickup', 'mappings', 'members'] as const,
  statuses: ['clickup', 'mappings', 'statuses'] as const,
  opportunityTypes: ['clickup', 'mappings', 'opportunity-types'] as const,
  customerTags: ['clickup', 'mappings', 'customer-tags'] as const,
  customerTagSources: ['clickup', 'options', 'customer-tag-sources'] as const,
  ticketTypes: ['clickup', 'options', 'ticket-types'] as const,
  ticketStatuses: ['clickup', 'options', 'ticket-statuses'] as const,
  membersOptions: ['clickup', 'options', 'members'] as const,
  opportunityTypeOptions: ['clickup', 'options', 'opportunity-types'] as const,
  discovery: (teamId?: string) => ['clickup', 'discovery', teamId ?? 'auto'] as const,
};

export function useClickupConfig(): UseQueryResult<ClickupConfig> {
  return useQuery({
    queryKey: keys.config,
    queryFn: () => api.get<ClickupConfig>('/integrations/clickup/config'),
  });
}

export function useUpdateClickupConfig(): UseMutationResult<
  ClickupConfig,
  unknown,
  UpdateClickupConfigInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.patch<ClickupConfig>('/integrations/clickup/config', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.config });
    },
  });
}

export function useClickupListMappings(): UseQueryResult<ClickupListMapping[]> {
  return useQuery({
    queryKey: keys.lists,
    queryFn: () => api.get<ClickupListMapping[]>('/integrations/clickup/mappings/lists'),
  });
}

export function useCreateClickupListMapping(): UseMutationResult<
  ClickupListMapping,
  unknown,
  { clickupListId: string; clickupListName: string; ticketTypeId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post<ClickupListMapping>('/integrations/clickup/mappings/lists', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.lists });
    },
  });
}

export function useDeleteClickupListMapping(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/integrations/clickup/mappings/lists/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.lists });
    },
  });
}

export function useClickupMemberMappings(): UseQueryResult<ClickupMemberMapping[]> {
  return useQuery({
    queryKey: keys.members,
    queryFn: () => api.get<ClickupMemberMapping[]>('/integrations/clickup/mappings/members'),
  });
}

export function useCreateClickupMemberMapping(): UseMutationResult<
  ClickupMemberMapping,
  unknown,
  { clickupMemberId: string; clickupMemberName: string; psaMemberId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post<ClickupMemberMapping>('/integrations/clickup/mappings/members', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.members });
    },
  });
}

export function useDeleteClickupMemberMapping(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/integrations/clickup/mappings/members/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.members });
    },
  });
}

export function useClickupStatusMappings(): UseQueryResult<ClickupStatusMapping[]> {
  return useQuery({
    queryKey: keys.statuses,
    queryFn: () => api.get<ClickupStatusMapping[]>('/integrations/clickup/mappings/statuses'),
  });
}

export function useCreateClickupStatusMapping(): UseMutationResult<
  ClickupStatusMapping,
  unknown,
  { clickupStatusCode: string; clickupStatusName: string; ticketStatusId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post<ClickupStatusMapping>('/integrations/clickup/mappings/statuses', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.statuses });
    },
  });
}

export function useDeleteClickupStatusMapping(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/integrations/clickup/mappings/statuses/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.statuses });
    },
  });
}

export function useClickupOpportunityTypeMappings(): UseQueryResult<
  ClickupOpportunityTypeMapping[]
> {
  return useQuery({
    queryKey: keys.opportunityTypes,
    queryFn: () =>
      api.get<ClickupOpportunityTypeMapping[]>('/integrations/clickup/mappings/opportunity-types'),
  });
}

export function useCreateClickupOpportunityTypeMapping(): UseMutationResult<
  ClickupOpportunityTypeMapping,
  unknown,
  {
    clickupSpaceId: string;
    clickupSpaceName: string;
    clickupFolderId?: string | null;
    clickupFolderName?: string | null;
    clickupListTemplateId?: string | null;
    clickupListTemplateName?: string | null;
    opportunityTypeId: string;
  }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post<ClickupOpportunityTypeMapping>(
        '/integrations/clickup/mappings/opportunity-types',
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.opportunityTypes });
    },
  });
}

export function useDeleteClickupOpportunityTypeMapping(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/integrations/clickup/mappings/opportunity-types/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.opportunityTypes });
    },
  });
}

/** Customer checkboxes (boolean fields + customer types) that can drive a ClickUp tag. */
export function useClickupCustomerTagSources(): UseQueryResult<ClickupCustomerTagSourceOption[]> {
  return useQuery({
    queryKey: keys.customerTagSources,
    queryFn: () =>
      api.get<ClickupCustomerTagSourceOption[]>('/integrations/clickup/customer-tag-sources'),
  });
}

export function useClickupCustomerTagMappings(): UseQueryResult<ClickupCustomerTagMapping[]> {
  return useQuery({
    queryKey: keys.customerTags,
    queryFn: () =>
      api.get<ClickupCustomerTagMapping[]>('/integrations/clickup/mappings/customer-tags'),
  });
}

export function useCreateClickupCustomerTagMapping(): UseMutationResult<
  ClickupCustomerTagMapping,
  unknown,
  CreateClickupCustomerTagMappingInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post<ClickupCustomerTagMapping>('/integrations/clickup/mappings/customer-tags', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.customerTags });
    },
  });
}

export function useDeleteClickupCustomerTagMapping(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/integrations/clickup/mappings/customer-tags/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.customerTags });
    },
  });
}

export function useTicketTypeOptions(): UseQueryResult<TicketType[]> {
  return useQuery({
    queryKey: keys.ticketTypes,
    queryFn: () => api.get<TicketType[]>('/tickets/type-options'),
  });
}

export function useTicketStatusOptions(): UseQueryResult<TicketStatus[]> {
  return useQuery({
    queryKey: keys.ticketStatuses,
    queryFn: () => api.get<TicketStatus[]>('/tickets/status-options'),
  });
}

export function useMemberOptions(): UseQueryResult<Member[]> {
  return useQuery({
    queryKey: keys.membersOptions,
    queryFn: async () => {
      const result = await api.get<PaginatedResult<Member>>(
        `/members${buildQuery({ page: 1, pageSize: 200, sortBy: 'displayName', sortDir: 'asc' })}`,
      );
      return result.data;
    },
  });
}

export function useOpportunityTypeOptions(): UseQueryResult<ServiceCategory[]> {
  return useQuery({
    queryKey: keys.opportunityTypeOptions,
    queryFn: async () => {
      const result = await api.get<PaginatedResult<ServiceCategory>>(
        `/service-categories${buildQuery({
          page: 1,
          pageSize: 200,
          sortBy: 'name',
          sortDir: 'asc',
          isActive: true,
        })}`,
      );
      return result.data;
    },
  });
}

export function useClickupDiscovery(
  teamId?: string,
  enabled = false,
): UseQueryResult<ClickupDiscoveryResponse> {
  const query = teamId ? buildQuery({ teamId }) : '';
  return useQuery({
    queryKey: keys.discovery(teamId),
    queryFn: () => api.get<ClickupDiscoveryResponse>(`/integrations/clickup/discovery${query}`),
    staleTime: 30_000,
    enabled,
  });
}

export function useClickupLogs(): UseQueryResult<ClickupLogEntry[]> {
  return useQuery({
    queryKey: ['clickup', 'logs'] as const,
    queryFn: () => api.get<ClickupLogEntry[]>('/integrations/clickup/logs'),
    refetchInterval: 15_000,
  });
}

export function useClickupWebhookAudit(filters: {
  outcome?: string;
  taskId?: string;
}): UseQueryResult<ClickupWebhookAuditEntry[]> {
  const query = buildQuery({
    limit: 100,
    ...(filters.outcome ? { outcome: filters.outcome } : {}),
    ...(filters.taskId ? { taskId: filters.taskId } : {}),
  });
  return useQuery({
    queryKey: ['clickup', 'audit', 'webhooks', filters.outcome ?? '', filters.taskId ?? ''] as const,
    queryFn: () =>
      api.get<ClickupWebhookAuditEntry[]>(`/integrations/clickup/audit/webhooks${query}`),
    refetchInterval: 30_000,
  });
}

export function useClickupWebhookAuditDetail(
  id: string | null,
): UseQueryResult<ClickupWebhookAuditDetail> {
  return useQuery({
    queryKey: ['clickup', 'audit', 'webhooks', 'detail', id ?? ''] as const,
    queryFn: () =>
      api.get<ClickupWebhookAuditDetail>(`/integrations/clickup/audit/webhooks/${id}`),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export function useClickupReconcileAudit(): UseQueryResult<ClickupReconcileAuditEntry[]> {
  return useQuery({
    queryKey: ['clickup', 'audit', 'reconciles'] as const,
    queryFn: () =>
      api.get<ClickupReconcileAuditEntry[]>(
        `/integrations/clickup/audit/reconciles${buildQuery({ limit: 100 })}`,
      ),
    refetchInterval: 30_000,
  });
}

export function useClickupReconcileAuditDetail(
  id: string | null,
): UseQueryResult<ClickupReconcileAuditDetail> {
  return useQuery({
    queryKey: ['clickup', 'audit', 'reconciles', 'detail', id ?? ''] as const,
    queryFn: () =>
      api.get<ClickupReconcileAuditDetail>(`/integrations/clickup/audit/reconciles/${id}`),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export function useClearClickupLogs(): UseMutationResult<{ cleared: true }, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ cleared: true }>('/integrations/clickup/logs'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clickup', 'logs'] });
    },
  });
}

export function useBackfillClickupTicketLinks(): UseMutationResult<
  ClickupBackfillLinksResult,
  unknown,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<ClickupBackfillLinksResult>('/integrations/clickup/backfill-ticket-links', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clickup', 'logs'] });
    },
  });
}

export function useBackfillClickupReferenceIds(): UseMutationResult<
  ClickupBackfillReferencesResult,
  unknown,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<ClickupBackfillReferencesResult>(
        '/integrations/clickup/backfill-reference-ids',
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clickup', 'logs'] });
    },
  });
}

export function useBackfillClickupPsaTicketLinks(): UseMutationResult<
  ClickupBackfillPsaLinkResult,
  unknown,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<ClickupBackfillPsaLinkResult>(
        '/integrations/clickup/backfill-psa-ticket-links',
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clickup', 'logs'] });
    },
  });
}

export function useClickupReconcile(): UseQueryResult<ClickupReconcileState> {
  return useQuery({
    queryKey: ['clickup', 'reconcile'] as const,
    queryFn: () => api.get<ClickupReconcileState>('/integrations/clickup/reconcile'),
  });
}

export function useUpdateClickupReconcile(): UseMutationResult<
  ClickupReconcileState,
  unknown,
  UpdateClickupReconcileInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.patch<ClickupReconcileState>('/integrations/clickup/reconcile', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clickup', 'reconcile'] });
    },
  });
}

export function useRunClickupReconcile(): UseMutationResult<ClickupReconcileResult, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ClickupReconcileResult>('/integrations/clickup/reconcile/run', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clickup', 'reconcile'] });
      void queryClient.invalidateQueries({ queryKey: ['clickup', 'logs'] });
    },
  });
}

export function useClickupWebhookQueue(): UseQueryResult<ClickupWebhookQueueStatus> {
  return useQuery({
    queryKey: ['clickup', 'webhook-queue'] as const,
    queryFn: () => api.get<ClickupWebhookQueueStatus>('/integrations/clickup/webhook-queue'),
    refetchInterval: 30_000,
  });
}

export function useDrainClickupWebhookQueue(): UseMutationResult<
  ClickupWebhookQueueDrainResult,
  unknown,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<ClickupWebhookQueueDrainResult>('/integrations/clickup/webhook-queue/drain', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clickup', 'webhook-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['clickup', 'logs'] });
    },
  });
}
